import type { LocalCookiePreference } from '../types/cookieConsent.types';
import apiClient from './api.service';

const STORAGE_KEY = 'pepoll_cookie_consent';

class CookieConsentService {
  getPreference(): LocalCookiePreference | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as LocalCookiePreference;
    } catch {
      return null;
    }
  }

  setPreference(marketing: boolean, version: string): LocalCookiePreference {
    const preference: LocalCookiePreference = {
      marketing,
      version,
      consentedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
    return preference;
  }

  clearPreference(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  isVersionOutdated(activeVersion: string): boolean {
    const preference = this.getPreference();
    if (!preference) return true;
    return preference.version !== activeVersion;
  }

  async fetchActivePolicyVersion(): Promise<string | null> {
    try {
      const response = await apiClient.get<{ data: { version: string } }>(
        '/api/public/legal/cookie-policy'
      );
      return response.data.data?.version ?? null;
    } catch {
      return null;
    }
  }

  async fetchProfileConsent(): Promise<{
    marketingConsent: boolean | null;
    cookiePolicyVersion: string | null;
    cookieConsentAt: string | null;
  } | null> {
    try {
      const response = await apiClient.get<{
        data: {
          marketingConsent: boolean | null;
          cookiePolicyVersion: string | null;
          cookieConsentAt: string | null;
        };
      }>('/api/profile/cookie-consent');
      return response.data.data ?? null;
    } catch {
      return null;
    }
  }

  async syncWithProfile(marketingConsent: boolean, cookiePolicyVersion: string): Promise<void> {
    try {
      await apiClient.patch('/api/profile/cookie-consent', {
        marketingConsent,
        cookiePolicyVersion,
      });
    } catch {
      // fire-and-forget: sync failure is non-critical
    }
  }
}

export const cookieConsentService = new CookieConsentService();
